import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck, FaClock, FaCommentAlt, FaImage, FaPause, FaPlay, FaRedoAlt, FaUser, FaUserCheck } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import {
  assignExternalMaintenanceTask,
  createExternalMaintenanceMessage,
  getExternalMaintenanceMessages,
  getExternalMaintenanceStaff,
  getExternalMaintenanceTask,
  maintenanceExternalError,
  updateExternalMaintenanceStatus
} from './maintenanceExternalApi';
import { clearMaintenanceSession, getMaintenanceSession } from './maintenanceExternalSession';
import {
  formatMaintenanceDate,
  MaintenanceErrorState,
  MaintenanceLinearLoading,
  MaintenancePageShell,
  maintenancePriorityLabel,
  maintenanceStatusLabel
} from './MaintenancePageShell';
import MaintenanceUpdateComposer from './MaintenanceUpdateComposer';

const plainText = value => {
  if (!value) return '';
  const node = document.createElement('div');
  node.innerHTML = String(value);
  return node.textContent || node.innerText || '';
};

export default function MaintenanceTaskDetailPage() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const [session] = useState(() => getMaintenanceSession());
  const centreId = Number(session?.selected_centre_id || 0);
  const selectedCentre = session?.centres?.find(centre => Number(centre.id) === centreId);
  const [task, setTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneeBusy, setAssigneeBusy] = useState(false);
  const [assigneeError, setAssigneeError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [error, setError] = useState('');
  const sequence = useRef(0);

  const load = useCallback(() => {
    if (!session?.token || !centreId || !/^\d+$/.test(String(issueId))) return () => {};
    const request = ++sequence.current;
    const controller = new AbortController();
    if (task) setRefreshing(true); else setLoading(true);
    setError('');
    Promise.all([
      getExternalMaintenanceTask(session.token, centreId, issueId, controller.signal),
      getExternalMaintenanceMessages(session.token, centreId, issueId, controller.signal)
    ])
      .then(([taskResult, messageResult]) => {
        if (request !== sequence.current) return;
        setTask(taskResult);
        setMessages(Array.isArray(messageResult) ? messageResult : []);
        setAssigneeId(taskResult?.assigned_to == null ? '' : String(taskResult.assigned_to));
      })
      .catch(reason => {
        if (request !== sequence.current || reason?.code === 'ERR_CANCELED') return;
        if (reason?.response?.status === 401) clearMaintenanceSession();
        setError(maintenanceExternalError(reason, 'This maintenance task could not be loaded.'));
      })
      .finally(() => {
        if (request !== sequence.current) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => controller.abort();
  }, [session?.token, centreId, issueId, Boolean(task)]);

  useEffect(() => load(), [session?.token, centreId, issueId]);

  useEffect(() => {
    if (!session?.token || !centreId) return undefined;
    const controller = new AbortController();
    setAssigneesLoading(true);
    setAssigneeError('');
    getExternalMaintenanceStaff(session.token, centreId, controller.signal)
      .then(result => setAssignees(Array.isArray(result) ? result : []))
      .catch(reason => {
        if (reason?.code === 'ERR_CANCELED') return;
        if (reason?.response?.status === 401) clearMaintenanceSession();
        setAssigneeError(maintenanceExternalError(reason, 'Maintenance members could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setAssigneesLoading(false);
      });
    return () => controller.abort();
  }, [session?.token, centreId]);

  if (!session) {
    return <MaintenancePageShell><MaintenanceErrorState title="Secure link required" message="Open the Maintenance link sent by your manager through WhatsApp." /></MaintenancePageShell>;
  }

  const saveUpdate = async payload => {
    await createExternalMaintenanceMessage(session.token, centreId, issueId, payload);
    load();
  };

  const changeStatus = async nextStatus => {
    setStatusBusy(true);
    setError('');
    try {
      await updateExternalMaintenanceStatus(session.token, centreId, issueId, nextStatus);
      setConfirmStatus(null);
      load();
    } catch (reason) {
      setError(maintenanceExternalError(reason, 'The task status could not be changed.'));
    } finally {
      setStatusBusy(false);
    }
  };

  const assignTask = async () => {
    const nextAssigneeId = Number(assigneeId);
    if (!Number.isSafeInteger(nextAssigneeId) || nextAssigneeId < 1) return;
    setAssigneeBusy(true);
    setAssigneeError('');
    try {
      const updatedTask = await assignExternalMaintenanceTask(session.token, centreId, issueId, nextAssigneeId);
      setTask(updatedTask);
      setAssigneeId(updatedTask?.assigned_to == null ? String(nextAssigneeId) : String(updatedTask.assigned_to));
    } catch (reason) {
      setAssigneeError(maintenanceExternalError(reason, 'This ticket could not be assigned.'));
    } finally {
      setAssigneeBusy(false);
    }
  };

  const headerAction = (
    <button type="button" className="maintenance-icon-button" onClick={load} aria-label="Refresh task" disabled={refreshing}>
      <FaRedoAlt className={refreshing ? 'is-spinning' : ''} aria-hidden="true" />
    </button>
  );

  const currentStatus = String(task?.status || '').toLowerCase();
  const currentAssigneeId = task?.assigned_to == null ? '' : String(task.assigned_to);
  const hasAssigneeChange = Boolean(assigneeId) && assigneeId !== currentAssigneeId;
  const actions = currentStatus === 'open'
    ? [{ status: 'in progress', label: 'Start work', icon: <FaPlay aria-hidden="true" /> }]
    : currentStatus === 'pending'
      ? [{ status: 'in progress', label: 'Resume work', icon: <FaPlay aria-hidden="true" /> }]
      : currentStatus === 'in progress'
        ? [{ status: 'pending', label: 'Waiting', icon: <FaPause aria-hidden="true" />, secondary: true }]
        : [];

  return (
    <MaintenancePageShell
      staffName={session.staff?.name}
      centreName={selectedCentre?.name}
      onBack={() => navigate('/maintenance/tasks')}
      action={headerAction}
    >
      {loading ? (
        <MaintenanceLinearLoading title="Opening maintenance task" detail="Loading details, photos, and activity." />
      ) : error && !task ? (
        <MaintenanceErrorState message={error} onRetry={load} />
      ) : task ? (
        <>
          {error && <p className="maintenance-inline-error maintenance-inline-error--card" role="alert">{error}</p>}
          <article className={`maintenance-detail-card ${refreshing ? 'is-refreshing' : ''}`} aria-busy={refreshing}>
            <div className="maintenance-detail-card__topline">
              <span className="maintenance-task-card__reference">{task.reference || `#${task.id}`}</span>
              <span className={`maintenance-priority priority-${Number(task.priority) || 0}`}>{maintenancePriorityLabel(task.priority)}</span>
            </div>
            <h1>{task.title || 'Maintenance task'}</h1>
            <div className="maintenance-detail-card__badges">
              <span className={`maintenance-status status-${String(task.status || 'open').replace(/\s+/g, '-')}`}>{maintenanceStatusLabel(task.status)}</span>
              <span>{task.category || 'Uncategorized'}</span>
            </div>
            <p className="maintenance-detail-card__description">{task.description || 'No description was provided.'}</p>
            <dl className="maintenance-detail-card__facts">
              <div><dt><FaUser aria-hidden="true" /> Reported by</dt><dd>{task.reported_by_name || 'Not provided'}</dd></div>
              <div><dt><FaClock aria-hidden="true" /> Created</dt><dd>{formatMaintenanceDate(task.created_at)}</dd></div>
            </dl>
          </article>

          <section className="maintenance-section-card maintenance-assignment-card" aria-labelledby="maintenance-assignment-title">
            <div className="maintenance-section-heading">
              <div><span className="maintenance-eyebrow">Ownership</span><h2 id="maintenance-assignment-title">Assign ticket</h2></div>
              <FaUserCheck aria-hidden="true" />
            </div>
            <p className="maintenance-assignment-card__current">Currently assigned to <strong>{task.assigned_to_name || 'nobody'}</strong>.</p>
            <div className="maintenance-assignment-card__control">
              <label htmlFor="maintenance-assignee">Maintenance member</label>
              <select
                id="maintenance-assignee"
                value={assigneeId}
                onChange={event => setAssigneeId(event.target.value)}
                disabled={assigneesLoading || assigneeBusy}
              >
                <option value="">{assigneesLoading ? 'Loading Maintenance members...' : 'Select a Maintenance member'}</option>
                {currentAssigneeId && !assignees.some(member => String(member.id) === currentAssigneeId) && (
                  <option value={currentAssigneeId}>{task.assigned_to_name || `Staff ${currentAssigneeId}`} (current)</option>
                )}
                {assignees.map(member => (
                  <option key={member.id} value={member.id}>{member.full_name || `Staff ${member.id}`}</option>
                ))}
              </select>
              <button type="button" className="maintenance-secondary-button" onClick={assignTask} disabled={!hasAssigneeChange || assigneeBusy || assigneesLoading}>
                <FaUserCheck aria-hidden="true" /> {assigneeBusy ? 'Assigning...' : 'Assign ticket'}
              </button>
            </div>
            {assigneeError && <p className="maintenance-inline-error" role="alert">{assigneeError}</p>}
          </section>

          {task.images?.length > 0 && (
            <section className="maintenance-section-card">
              <div className="maintenance-section-heading"><div><span className="maintenance-eyebrow">Issue photos</span><h2>Attachments</h2></div><FaImage aria-hidden="true" /></div>
              <div className="maintenance-gallery">
                {task.images.map((image, index) => (
                  <a key={image.id || image.url} href={image.url} target="_blank" rel="noreferrer">
                    <img src={image.url} alt={`Issue attachment ${index + 1}`} />
                  </a>
                ))}
              </div>
            </section>
          )}

          <section className="maintenance-section-card">
            <div className="maintenance-section-heading"><div><span className="maintenance-eyebrow">History</span><h2>Activity</h2></div><span>{messages.length}</span></div>
            {messages.length ? (
              <ol className="maintenance-timeline">
                {[...messages].reverse().map(message => (
                  <li key={message.id}>
                    <div className="maintenance-timeline__icon"><FaCommentAlt aria-hidden="true" /></div>
                    <div>
                      <div className="maintenance-timeline__meta"><strong>{message.created_by_name}</strong><span>{formatMaintenanceDate(message.created_at)}</span></div>
                      <p>{plainText(message.message) || 'Photo update'}</p>
                      {message.images?.length > 0 && (
                        <div className="maintenance-timeline__images">
                          {message.images.map((image, index) => <a key={image.id || image.url} href={image.url} target="_blank" rel="noreferrer"><img src={image.url} alt={`Update attachment ${index + 1}`} /></a>)}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : <div className="maintenance-empty-state"><h3>No comments yet</h3><p>Add the first comment below.</p></div>}
          </section>

          <MaintenanceUpdateComposer issueId={issueId} onSave={saveUpdate} />

          {currentStatus !== 'completed' && (
            <section className="maintenance-status-actions" aria-label="Task status actions">
              {actions.map(action => (
                <button
                  key={action.status}
                  type="button"
                  className={action.secondary ? 'maintenance-secondary-button' : 'maintenance-primary-button'}
                  onClick={() => setConfirmStatus(action)}
                  disabled={statusBusy}
                >
                  {action.icon}{action.label}
                </button>
              ))}
              <button type="button" className="maintenance-complete-button" onClick={() => setConfirmStatus({ status: 'completed', label: 'Mark completed' })} disabled={statusBusy}>
                <FaCheck aria-hidden="true" /> Mark completed
              </button>
            </section>
          )}

          {confirmStatus && (
            <div className="maintenance-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setConfirmStatus(null); }}>
              <section className="maintenance-dialog" role="dialog" aria-modal="true" aria-labelledby="maintenance-status-dialog-title">
                <span className="maintenance-eyebrow">Confirm status</span>
                <h2 id="maintenance-status-dialog-title">{confirmStatus.label}?</h2>
                <p>This will update the task immediately and notify the relevant Care Guardian team members.</p>
                <div>
                  <button type="button" className="maintenance-secondary-button" onClick={() => setConfirmStatus(null)} disabled={statusBusy}>Cancel</button>
                  <button type="button" className="maintenance-primary-button" onClick={() => changeStatus(confirmStatus.status)} disabled={statusBusy}>{statusBusy ? 'Updating...' : 'Confirm'}</button>
                </div>
              </section>
            </div>
          )}
        </>
      ) : null}
    </MaintenancePageShell>
  );
}
